import React, { useState } from 'react';
import { HORDE_DECKS } from '@mtg-deck-tool/shared';
import type { HordeDeckData } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { api } from '../../lib/api-client.js';

interface HordeSetupScreenProps {
  onCancel: () => void;
}

const DECK_SIZES = [25, 50, 75, 100] as const;
type DeckSize = (typeof DECK_SIZES)[number];

export function HordeSetupScreen({ onCancel }: HordeSetupScreenProps) {
  const initHorde = usePlaytesterStore((s) => s.initHorde);

  const [selectedDeck, setSelectedDeck] = useState<HordeDeckData>(() => HORDE_DECKS[0]!);
  const [selectedSize, setSelectedSize] = useState<DeckSize>(25);
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleStart() {
    setLoading(true);
    setWarning(null);
    setError(null);

    try {
      const { cards, missing } = await api.resolveHordeDeck(selectedDeck.cards);

      if (missing.length > 0) {
        setWarning(
          `${missing.length} card${missing.length === 1 ? '' : 's'} could not be resolved and will be skipped: ${missing.join(', ')}`,
        );
      }

      initHorde(selectedDeck, cards, selectedSize);
      // onCancel closes the setup screen; PlaytesterView will render horde UI
      onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load horde deck.');
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80">
      <div className="bg-gray-900 border border-gray-700 rounded-lg w-full max-w-2xl mx-4 p-6 flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-white text-xl font-bold">vs Horde — Setup</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white text-lg font-bold px-2"
            aria-label="Cancel"
          >
            ✕
          </button>
        </div>

        {/* Deck selection */}
        <div className="flex flex-col gap-2">
          <p className="text-gray-400 text-sm font-medium uppercase tracking-wide">
            Choose a Horde Deck
          </p>
          <div className="flex flex-col gap-2">
            {HORDE_DECKS.map((deck) => {
              const totalCards = deck.cards.reduce((s, e) => s + e.quantity, 0);
              const isSelected = deck.id === selectedDeck.id;
              return (
                <button
                  key={deck.id}
                  onClick={() => setSelectedDeck(deck)}
                  className={[
                    'w-full text-left px-4 py-3 rounded-lg border transition-colors',
                    isSelected
                      ? 'border-orange-500 bg-orange-950/40 text-white'
                      : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-500',
                  ].join(' ')}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{deck.name}</span>
                    <span className="text-xs text-gray-500">{totalCards} cards</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{deck.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Size selector */}
        <div className="flex flex-col gap-2">
          <p className="text-gray-400 text-sm font-medium uppercase tracking-wide">
            Deck Size (cards drawn from top of shuffled deck)
          </p>
          <div className="flex gap-2">
            {DECK_SIZES.map((size) => (
              <button
                key={size}
                onClick={() => setSelectedSize(size)}
                className={[
                  'flex-1 py-2 rounded-lg border text-sm font-medium transition-colors',
                  selectedSize === size
                    ? 'border-orange-500 bg-orange-700 text-white'
                    : 'border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-500',
                ].join(' ')}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        {/* Warnings / errors */}
        {warning && (
          <div className="bg-yellow-900/40 border border-yellow-600 rounded p-3 text-yellow-200 text-sm">
            {warning}
          </div>
        )}
        {error && (
          <div className="bg-red-900/40 border border-red-600 rounded p-3 text-red-200 text-sm">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm"
          >
            Cancel
          </button>
          <button
            onClick={() => void handleStart()}
            disabled={loading}
            className="px-5 py-2 rounded bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white text-sm font-medium"
          >
            {loading ? 'Loading…' : 'Start Horde Game'}
          </button>
        </div>
      </div>
    </div>
  );
}
